import { PASSOS } from '../dados/passos';
import { Secao } from './secao';
import { Reveal, RevealGrupo } from './reveal';
import { useEmVista } from '../hooks/use-em-vista';

/**
 * Linha vertical que "se desenha" (scaleY 0→1, ancorada no topo) conforme os
 * passos entram na viewport — o único elemento decorativo extra desta seção.
 *
 * ## Por que a visibilidade vem de fora
 * Ela observava a si mesma, e nunca crescia. `scaleY(0)` COLAPSA a caixa: o
 * `getBoundingClientRect` da linha era 0×0, o `IntersectionObserver` media
 * proporção zero, e o limiar de 20% jamais era alcançado — a linha esperava
 * por uma visibilidade que o próprio transform impedia de existir. Quem
 * observa agora é o contêiner dos passos, que tem altura de verdade.
 *
 * A duração e a curva vêm de `.linha-cresce` (`estilos/motion.css`), não de
 * classes utilitárias: 700ms/`ease-out` era o único tempo da página fora do
 * vocabulário, e número solto é como duas animações irmãs acabam com ritmos
 * diferentes.
 */
function LinhaConectora({ visivel }: { visivel: boolean }) {
  return (
    <div
      aria-hidden
      data-visivel={visivel ? 'true' : 'false'}
      className="linha-cresce bg-auth-borda/15 absolute top-2 bottom-2 left-[16px] hidden w-px sm:block"
    />
  );
}

export function ComoFunciona(): JSX.Element {
  const { ref, emVista } = useEmVista<HTMLDivElement>(0.2);

  return (
    <Secao
      id="como-funciona"
      rotulo="Como funciona"
      titulo={
        <>
          Você cadastra a sociedade. O contrato sai{' '}
          <span className="font-semibold">pronto</span>.
        </>
      }
      conduz="Enquanto o documento for a fonte da verdade, revisar com mais cuidado só adia o próximo erro. Aqui são três passos, nesta ordem — cada um só é possível porque o anterior guardou dado, e não texto."
    >
      <div ref={ref} className="relative mt-12">
        <LinhaConectora visivel={emVista} />
        <RevealGrupo className="flex flex-col gap-6" passoMs={80}>
          {PASSOS.map((passo, indice) => {
            const Icone = passo.icone;
            // Vêm da ESQUERDA, acompanhando a linha que cresce ao lado: os
            // três passos são uma sequência, não uma grade.
            return (
              <Reveal
                key={passo.titulo}
                direcao="esquerda"
                className="relative flex gap-6 pl-0 sm:pl-12"
              >
                <span
                  aria-hidden
                  className="border-auth-borda/15 bg-auth-fundo-1 text-auth-marca-hover absolute top-0 left-0 hidden h-8 w-8 items-center justify-center rounded-full border font-mono text-xs sm:flex"
                >
                  {String(indice + 1).padStart(2, '0')}
                </span>
                <div className="group border-auth-borda/8 bg-auth-superficie/[0.02] rounded-auth-card cartao-reativo hover:border-auth-borda/20 hover:shadow-auth-profundidade flex flex-1 flex-col gap-2 border p-5">
                  <Icone
                    size={20}
                    className="text-auth-marca-hover transition-[transform,color] duration-300 ease-out group-hover:-translate-y-px"
                    aria-hidden
                  />
                  <h3 className="text-auth-texto text-lg font-medium tracking-tight">
                    <span className="text-auth-marca-hover mr-2 font-mono text-sm sm:hidden">
                      {String(indice + 1).padStart(2, '0')}
                    </span>
                    {passo.titulo}
                  </h3>
                  <p className="text-auth-texto-secundario max-w-xl text-sm leading-relaxed">
                    {passo.texto}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </RevealGrupo>
      </div>
    </Secao>
  );
}
